import { useMemo } from 'react';
import {
  TableProps,
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  TableSortLabel,
} from '@mui/material';
import CustomTableCell from '@/components/general/CustomTableCell';
import { HeaderTable, TableColumnOrder } from '@/types';
import styles from './CustomTable.styles';

interface CustomTableProps<CustomType> extends TableProps {
  dataLength: number;
  rowsPerPage: number;
  currentPage: number;
  emptyRowsQuantity: number;
  orderBy: string | number | symbol;
  order: TableColumnOrder;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleRequestSort: (event: React.MouseEvent<HTMLSpanElement>, property: keyof CustomType) => void;
  headers?: React.ReactNode;
  headersArray?: HeaderTable<CustomType>[] | undefined;
  toolbar?: React.ReactNode;
}

const CustomTableComponent = function <CustomType>({
  dataLength,
  rowsPerPage,
  currentPage,
  emptyRowsQuantity,
  orderBy,
  order,
  handleChangePage,
  handleChangeRowsPerPage,
  handleRequestSort,
  headers,
  headersArray,
  toolbar,
  ...props
}: CustomTableProps<CustomType>): JSX.Element {
  const renderHeaders = useMemo(() => {
    if (!headersArray) return headers;

    const headerElements = headersArray?.map((header, index) => {
      if (!header) {
        const emptyCellKey = `empty-${headersArray.length + index}`;
        return <CustomTableCell key={emptyCellKey} />;
      }

      return (
        <CustomTableCell
          key={String(header.id)}
          sortDirection={orderBy === header.id ? order : false}
        >
          <TableSortLabel
            active={orderBy === header.id}
            direction={orderBy === header.id ? order : 'asc'}
            onClick={(event) => handleRequestSort(event, header.id)}
            sx={styles.header}
          >
            {header.label}
          </TableSortLabel>
        </CustomTableCell>
      );
    });

    return headerElements;
  }, [handleRequestSort, headers, headersArray, order, orderBy]);

  const renderContent = useMemo(() => {
    if (dataLength === 0) {
      return (
        <TableRow>
          <CustomTableCell align="center" colSpan={12}>
            <Typography variant="h6" color="primary.light">
              No data
            </Typography>
          </CustomTableCell>
        </TableRow>
      );
    }

    return (
      <>
        {props.children}

        {emptyRowsQuantity > 0 && (
          <TableRow
            sx={{
              height: 53 * emptyRowsQuantity,
            }}
          >
            <CustomTableCell colSpan={6} />
          </TableRow>
        )}
      </>
    );
  }, [dataLength, emptyRowsQuantity, props.children]);

  return (
    <Box sx={styles.container}>
      <TableContainer component={Paper} variant="outlined">
        {toolbar}

        <Table sx={styles.table} {...props}>
          <TableHead>
            <TableRow>{renderHeaders}</TableRow>
          </TableHead>

          <TableBody>{renderContent}</TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10]}
          component="div"
          count={dataLength}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default CustomTableComponent;
